import { createContext, useContext } from 'react';
import type React from 'react';
import type {
  Connection,
  Edge,
  EdgeChange,
  Node,
  NodeChange,
  OnSelectionChangeParams,
  ReactFlowInstance,
} from 'reactflow';
import type { Device, ExecutionLog, ExecutionStep } from '@/stores/devices/device-store';
import type { SchemaDefinition } from '@/stores/schemas/schema-store';
import type { Screen } from '@/stores/screens/screen-store';
import type {
  DeviceVariantId,
  ExecutionTarget,
  NodeDefinition,
  NodesByCategory,
} from '@/types/workflow-nodes';
import type {
  JsonValue,
  WorkflowAvailableVariable,
  WorkflowDefinition,
  WorkflowDefinitionVariable,
  WorkflowNodeConfig,
  WorkflowNodeData,
  WorkflowValidationResult,
} from '@/types/workflow-editor';

export interface WorkflowEditorContextValue {
  workflowId: string | undefined;
  workflow: WorkflowDefinition | null;
  setWorkflow: React.Dispatch<React.SetStateAction<WorkflowDefinition | null>>;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  embedded: boolean;
  projectId: string | undefined;
  nodes: Node<WorkflowNodeData>[];
  setNodes: React.Dispatch<React.SetStateAction<Node<WorkflowNodeData>[]>>;
  onNodesChange: (changes: NodeChange[]) => void;
  edges: Edge[];
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  onEdgesChange: (changes: EdgeChange[]) => void;
  selectedNodes: Node<WorkflowNodeData>[];
  setSelectedNodes: React.Dispatch<React.SetStateAction<Node<WorkflowNodeData>[]>>;
  selectedNode: Node<WorkflowNodeData> | null;
  workflowVariables: WorkflowDefinitionVariable[];
  setWorkflowVariables: React.Dispatch<React.SetStateAction<WorkflowDefinitionVariable[]>>;
  allAvailableVariables: WorkflowAvailableVariable[];
  rightSidebarTab: 'properties' | 'variables' | 'execution' | 'ai-chat' | 'modification-log';
  setRightSidebarTab: React.Dispatch<React.SetStateAction<'properties' | 'variables' | 'execution' | 'ai-chat' | 'modification-log'>>;
  leftSidebarTab: 'nodes' | 'screens' | 'schemas' | 'secrets';
  setLeftSidebarTab: React.Dispatch<React.SetStateAction<'nodes' | 'screens' | 'schemas' | 'secrets'>>;
  validationResult: WorkflowValidationResult | null;
  setValidationResult: React.Dispatch<React.SetStateAction<WorkflowValidationResult | null>>;
  isValidating: boolean;
  showVariablePicker: boolean;
  setShowVariablePicker: React.Dispatch<React.SetStateAction<boolean>>;
  expressionInputRef: React.RefObject<HTMLInputElement>;
  debugModeEnabled: boolean;
  setDebugModeEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  serverDebugMode: boolean;
  lastExecutionId: string | null;
  nodeDebugState: Map<string, { lastStatus: 'completed' | 'error' | 'started' | 'skipped' | null; executionCount: number }>;
  sampleParseError: string | null;
  setSampleParseError: React.Dispatch<React.SetStateAction<string | null>>;
  effectiveExecutionKey: string | undefined;
  onDemandSchema: SchemaDefinition | null;
  selectedDeviceId: string | null;
  setSelectedDeviceId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedVariantId: DeviceVariantId | null;
  setSelectedVariantId: React.Dispatch<React.SetStateAction<DeviceVariantId | null>>;
  nodeRegistryLoading: boolean;
  selectedTarget: ExecutionTarget;
  setSelectedTarget: (target: ExecutionTarget) => void;
  isDataSource: boolean;
  setIsDataSource: (value: boolean) => void;
  getFilteredNodesByCategory: () => NodesByCategory[];
  getNodeDefinition: (type: string) => NodeDefinition | undefined;
  devices: Device[];
  deployWorkflow: (deviceId: string, workflowId: string) => Promise<{ success: boolean; queued?: boolean }>;
  getExecutionSteps: (deviceId: string) => ExecutionStep[];
  clearExecutionLogs: (key: string) => void;
  setDeviceLogging: (deviceId: string, enabled: boolean) => void;
  executionSteps: Map<string, ExecutionStep[]>;
  executionLogs: Map<string, ExecutionLog[]>;
  dataFilterOptions: {
    dataTypes: string[];
    esp32WorkflowIds: string[];
    customIds: string[];
    esp32Workflows: Array<{ id: string; name: string; status: string }>;
  };
  fetchDataFilterOptions: () => void;
  screens: Screen[];
  fetchScreens: () => void;
  openSchemaBuilder: () => void;
  schemas: SchemaDefinition[];
  fetchSchemas: () => void;
  getSchema: (id: string) => Promise<SchemaDefinition | null>;
  reactFlowWrapper: React.RefObject<HTMLDivElement>;
  onConnect: (params: Connection) => void;
  onNodeClick: (event: React.MouseEvent, node: Node) => void;
  onPaneClick: () => void;
  onSelectionChange: (params: OnSelectionChangeParams) => void;
  onEdgeClick: (event: React.MouseEvent, edge: Edge) => void;
  onEdgesDelete: (edges: Edge[]) => void;
  onReconnect: (oldEdge: Edge, newConnection: Connection) => void;
  onInit: (instance: ReactFlowInstance) => void;
  onDragOver: (event: React.DragEvent) => void;
  onDrop: (event: React.DragEvent) => void;
  handleSave: () => Promise<boolean>;
  handleExecute: () => Promise<void>;
  handleCancelExecution: () => Promise<void>;
  isExecuting: boolean;
  handleBack: () => void;
  handleDeployToDevice: () => Promise<void>;
  handlePublishToggle: (shouldPublish: boolean) => Promise<void>;
  handleParseSampleMessage: () => void;
  handleValidateExpression: () => Promise<void>;
  handleInsertVariable: (variableName: string) => void;
  toggleServerDebugMode: (enabled: boolean) => Promise<void>;
  deleteSelectedNodes: () => void;
  updateNodeData: (field: string, value: JsonValue | undefined) => void;
  updateNodeParameter: (paramName: string, value: JsonValue | undefined) => void;
  updateNodeConfig: <K extends keyof WorkflowNodeConfig>(configName: K, value: WorkflowNodeConfig[K]) => void;
  getAvailableVariables: () => Array<{ name: string; type: string; source: string }>;
  extractJsonPaths: (obj: unknown, prefix?: string) => string[];
  pathToVariableName: (path: string) => string;
  environmentsDialogOpen: boolean;
  openEnvironmentsDialog: () => void;
  closeEnvironmentsDialog: () => void;
  availableEnvironments: string[];
  handleSaveEnvironments: (environments: string[]) => Promise<void>;
  convertToAgent: () => Promise<void>;
  onClose?: () => void;
}

export const WorkflowEditorContext = createContext<WorkflowEditorContextValue | null>(null);

export function useWorkflowEditor(): WorkflowEditorContextValue {
  const ctx = useContext(WorkflowEditorContext);
  if (!ctx) {
    throw new Error('useWorkflowEditor must be used within a WorkflowEditorProvider');
  }

  return ctx;
}
